// modules/reporteImpuestos/pages/ReporteImpuestosPage.jsx
// Migracion de reporte_impuestos.php (PHP legado) — a diferencia de
// Reporte de Facturas (que lee solo la BD local), este consulta el listado
// real de facturas emitidas en el SIAT via la API de Hippo para un rango de
// fechas, y lo cruza por CUF con la BD local para completar cliente,
// servicio, sucursal y montos.
import React, { useMemo, useState } from "react";
import {
  Box, Button, TextField, MenuItem, Typography, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  TableSortLabel, Stack,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { useAuth } from "../../../context/AuthContext";
import { getReporteImpuestos, getPdfPorCuf, exportarExcel } from "../services/reporteImpuestosService";
import { imprimirFactura } from "../../facturacion/services/facturacionService";

const StyledHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  fontSize: "0.72rem",
  whiteSpace: "nowrap",
  padding: "3px 6px",
  backgroundColor: theme.palette.mode === "dark"
    ? theme.palette.grey[800] : theme.palette.grey[100],
}));

const celdaTruncada = { maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 32, fontSize: "0.8rem" },
  "& .MuiInputBase-input": { py: 0.65 },
  "& .MuiInputLabel-root": { fontSize: "0.78rem" },
  "& .MuiSelect-select": { py: "6px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

const SUCURSALES = ["Todos", "La Paz", "Cochabamba", "Santa Cruz"];

const ESTADOS_EXCLUIDOS = ["ANULADA", "ERROR", "CANCELADA"];

const normalizarSucursal = (value) => {
  const s = String(value || "").trim().toLowerCase();
  if (s.includes("santa cruz")) return "Santa Cruz";
  if (s.includes("cochabamba")) return "Cochabamba";
  if (s.includes("la paz") || s.includes("el alto")) return "La Paz";
  return "Todos";
};

const hoyISO = () => new Date().toISOString().slice(0, 10);
const haceUnMesISO = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 10);
};

// Coma para miles, punto para decimales — mismo formato que number_format()
// de PHP (usado tal cual en todos los reportes legados que se migraron).
const formatMoneda = (value) =>
  value == null ? "-" : Number(value).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatFechaHora = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const colorEstado = (estado) => {
  if (ESTADOS_EXCLUIDOS.includes(estado)) return "error";
  if (estado === "PENDIENTE") return "warning";
  if (estado === "VALIDADA") return "success";
  return "default";
};

const getValorOrdenable = (row, col) => {
  if (col === "fechaEmision") return row.fechaEmision ? new Date(row.fechaEmision).getTime() : 0;
  return row[col];
};

const compararValores = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "string" || typeof b === "string") {
    return String(a).localeCompare(String(b), "es", { numeric: true, sensitivity: "base" });
  }
  return a - b;
};

const SortableHeaderCell = ({ col, label, align, orderBy, orderDir, onSort }) => (
  <StyledHeaderCell align={align} sortDirection={orderBy === col ? orderDir : false}>
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? orderDir : "asc"}
      onClick={() => onSort(col)}
    >
      {label}
    </TableSortLabel>
  </StyledHeaderCell>
);

const COLUMNAS = [
  { col: "cliente", label: "Cliente" },
  { col: "servicio", label: "Servicio" },
  { col: "nombreFactura", label: "Nombre de factura" },
  { col: "numeroFactura", label: "N° Factura" },
  { col: "fechaEmision", label: "Fecha Emisión" },
  { col: "montoTotalHippo", label: "Monto Total", align: "right" },
  { col: "montoTotalLocal", label: "Monto Total (SORT)", align: "right" },
  { col: "mes", label: "Mes" },
  { col: "sucursal", label: "Sucursal" },
  { col: "cuf", label: "CUF" },
  { col: "codServicioCliente", label: "Cod Cliente" },
  { col: "saldo", label: "Saldo", align: "right" },
  { col: "pagada", label: "Pagada" },
  { col: "estado", label: "Estado" },
];

const ReporteImpuestosPage = () => {
  const { user } = useAuth();
  const [fechaIni, setFechaIni] = useState(haceUnMesISO);
  const [fechaFin, setFechaFin] = useState(hoyISO);
  const [sucursal, setSucursal] = useState(() => normalizarSucursal(user?.sucursal));
  const [data, setData] = useState(null);
  const [buscado, setBuscado] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [abriendoCuf, setAbriendoCuf] = useState(null);
  const [orderBy, setOrderBy] = useState("fechaEmision");
  const [orderDir, setOrderDir] = useState("asc");
  const [exportando, setExportando] = useState(false);

  const handleBuscar = async () => {
    setLoading(true);
    setError("");
    try {
      const { data: resp } = await getReporteImpuestos({ fechaIni, fechaFin, sucursal });
      setData(resp);
      setBuscado(true);
    } catch (e) {
      setData(null);
      setError(e?.response?.data?.error || "No se pudo generar el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportar = async () => {
    setExportando(true);
    setError("");
    try {
      const response = await exportarExcel({ fechaIni, fechaFin, sucursal });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "reporte_impuestos.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError("No se pudo exportar el reporte a Excel.");
    } finally {
      setExportando(false);
    }
  };

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrderDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(col);
      setOrderDir("asc");
    }
  };

  const itemsOrdenados = useMemo(() => {
    const items = data?.items || [];
    if (!orderBy) return items;
    const copia = [...items].sort((a, b) =>
      compararValores(getValorOrdenable(a, orderBy), getValorOrdenable(b, orderBy))
    );
    if (orderDir === "desc") copia.reverse();
    return copia;
  }, [data, orderBy, orderDir]);

  const handleVerFactura = async (row) => {
    const key = row.cod ?? row.cuf;
    setAbriendoCuf(key);
    try {
      const response = row.cod
        ? await imprimirFactura(row.cod)
        : await getPdfPorCuf(row.cuf);
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setError("No se pudo abrir la factura.");
    } finally {
      setAbriendoCuf(null);
    }
  };

  const sucursales = Object.keys(data?.totalesPorSucursal || {});

  return (
    <DashboardLayout>
      <PageHeader
        title="Reporte de Impuestos"
        subtitle="Facturas emitidas al SIAT (vía Hippo) por rango de fechas"
        backTo="/reporte-facturas"
      />
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2, flexWrap: "wrap" }}>
          <TextField
            type="date" size="small" label="Fecha inicio"
            sx={{ ...compactFieldSx, width: 165 }}
            value={fechaIni}
            onChange={(e) => setFechaIni(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            type="date" size="small" label="Fecha fin"
            sx={{ ...compactFieldSx, width: 165 }}
            value={fechaFin}
            onChange={(e) => setFechaFin(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select size="small" label="Sucursal"
            sx={{ ...compactFieldSx, width: 150 }}
            value={sucursal}
            onChange={(e) => setSucursal(e.target.value)}
          >
            {SUCURSALES.map((s) => (
              <MenuItem key={s} value={s} sx={{ fontSize: "0.85rem" }}>{s}</MenuItem>
            ))}
          </TextField>
          <Button
            size="small"
            variant="contained"
            startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{ minHeight: 32 }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileDownloadIcon fontSize="small" />}
            onClick={handleExportar}
            disabled={exportando || !buscado || !(data?.items?.length)}
            sx={{ minHeight: 32, textTransform: "none" }}
          >
            {exportando ? "Exportando..." : "Excel"}
          </Button>
          {buscado && (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
              Total: {data?.items?.length || 0}
            </Typography>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!buscado ? (
          <Alert severity="info">Elige un rango de fechas y presiona Buscar.</Alert>
        ) : !loading && (data?.items?.length || 0) === 0 ? (
          <Alert severity="info">No se encontraron facturas para los filtros seleccionados.</Alert>
        ) : (
          <Paper variant="outlined">
            <TableContainer sx={{ maxHeight: "65vh" }}>
              <Table
                size="small"
                stickyHeader
                sx={{
                  "& td": { py: 0.35, px: 0.75, fontSize: "0.74rem", whiteSpace: "nowrap" },
                }}
              >
                <TableHead>
                  <TableRow>
                    {COLUMNAS.map((c) => (
                      <SortableHeaderCell key={c.col} {...c} orderBy={orderBy} orderDir={orderDir} onSort={handleSort} />
                    ))}
                    <StyledHeaderCell>Acción</StyledHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNAS.length + 1} align="center" sx={{ py: 3 }}>
                        <CircularProgress size={24} />
                      </TableCell>
                    </TableRow>
                  ) : (
                    itemsOrdenados.map((r) => (
                      <TableRow
                        key={r.cuf || r.numeroFactura}
                        hover
                        sx={r.esAnulada ? { bgcolor: (t) => alpha(t.palette.error.main, 0.08) } : undefined}
                      >
                        <TableCell sx={celdaTruncada} title={r.cliente}>{r.cliente || "-"}</TableCell>
                        <TableCell sx={{ ...celdaTruncada, maxWidth: 90 }} title={r.servicio}>{r.servicio || "-"}</TableCell>
                        <TableCell sx={celdaTruncada} title={r.nombreFactura}>{r.nombreFactura || "-"}</TableCell>
                        <TableCell>{r.numeroFactura}</TableCell>
                        <TableCell>{formatFechaHora(r.fechaEmision)}</TableCell>
                        <TableCell align="right">{formatMoneda(r.montoTotalHippo)}</TableCell>
                        <TableCell align="right">{formatMoneda(r.montoTotalLocal)}</TableCell>
                        <TableCell>{r.mes || "-"}</TableCell>
                        <TableCell>{r.sucursal || "-"}</TableCell>
                        <TableCell>
                          <Typography variant="caption" title={r.cuf} sx={{ display: "block", ...celdaTruncada, maxWidth: 130 }}>
                            {r.cuf}
                          </Typography>
                        </TableCell>
                        <TableCell>{r.codServicioCliente ?? "-"}</TableCell>
                        <TableCell align="right">{formatMoneda(r.saldo)}</TableCell>
                        <TableCell>{r.pagada || "-"}</TableCell>
                        <TableCell>
                          <Typography variant="caption" color={`${colorEstado(r.estado)}.main`} sx={{ fontWeight: 700 }}>
                            {r.estado || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="small"
                            variant="text"
                            disabled={abriendoCuf === (r.cod ?? r.cuf) || (!r.cod && !r.cuf)}
                            onClick={() => handleVerFactura(r)}
                            sx={{ fontSize: "0.72rem", textTransform: "none", p: 0, minWidth: 0, minHeight: 0, lineHeight: 1.6 }}
                          >
                            {abriendoCuf === (r.cod ?? r.cuf) ? "Abriendo..." : "Ver Factura"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {buscado && data && sucursales.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Resumen por sucursal
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1, fontWeight: 400 }}>
                (no incluye facturas anuladas)
              </Typography>
            </Typography>
            <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
              {sucursales.map((s) => (
                <Paper key={s} variant="outlined" sx={{ p: 1.5, minWidth: 160 }}>
                  <Typography variant="caption" color="text.secondary">{s}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Bs. {formatMoneda(data.totalesPorSucursal[s])}
                  </Typography>
                </Paper>
              ))}
              {sucursales.length > 1 && (
                <Paper variant="outlined" sx={{ p: 1.5, minWidth: 160, bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}>
                  <Typography variant="caption" color="primary.main" sx={{ fontWeight: 600 }}>TOTAL GENERAL</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    Bs. {formatMoneda(data.totalGeneral)}
                  </Typography>
                </Paper>
              )}
            </Stack>
            {data.contadorAnuladas > 0 && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {data.contadorAnuladas} factura(s) anulada(s) no incluidas en los totales.
              </Alert>
            )}
          </Paper>
        )}
      </Box>
    </DashboardLayout>
  );
};

export default ReporteImpuestosPage;
